import React from 'react';

/*
 * Tome ornaments, drawn as inline SVG rather than Unicode glyphs (the vendored
 * fonts carry narrow ranges, so ❖ ✦ and runes would fall back to system fonts).
 * All decorative: aria-hidden, no layout of their own beyond their box.
 */

export type TomeCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/** Creeping vine corner, mirrored per corner; positioned by the parent's CSS (`[data-corner]`). */
export const TomeVineCorner: React.FC<{ corner: TomeCorner; className?: string }> = ({ corner, className = '' }) => {
  const flipX = corner.endsWith('right') ? -1 : 1;
  const flipY = corner.startsWith('bottom') ? -1 : 1;
  return <svg className={`tome-vine ${className}`} data-corner={corner} viewBox="0 0 80 80" fill="none" aria-hidden="true"
    style={{ transform: `scale(${flipX}, ${flipY})` }}>
    <path d="M -4 42 Q 6 18 36 12 Q 58 8 78 4" stroke="#162e1c" strokeWidth="3.2" strokeLinecap="round" />
    <path d="M -4 42 Q 6 18 36 12 Q 58 8 78 4" stroke="#3d6b41" strokeWidth="1.6" strokeLinecap="round" opacity="0.85" />
    <path d="M 28 14 Q 38 2 48 8 Q 52 14 44 18 Q 40 16 42 12" stroke="#558842" strokeWidth="1.2" strokeLinecap="round" />
    <g transform="translate(18, 16) rotate(-25) scale(0.75)">
      <path d="M 0 0 C -6 -12, 10 -14, 13 -3 C 10 6, -3 5, 0 0 Z" fill="#204629" />
      <path d="M 0 0 C -4 -8, 7 -9, 9 -2" stroke="#6ca864" strokeWidth="0.8" />
    </g>
    <g transform="translate(54, 8) rotate(15) scale(0.6)">
      <path d="M 0 0 C -6 -12, 10 -14, 13 -3 C 10 6, -3 5, 0 0 Z" fill="#30613a" />
      <path d="M 0 0 C -4 -8, 7 -9, 9 -2" stroke="#8cd27b" strokeWidth="0.8" />
      <circle cx="12" cy="-2" r="1.8" fill="#f0d18a" opacity="0.85" />
    </g>
    <g transform="translate(2, 34) rotate(-65) scale(0.55)">
      <path d="M 0 0 C -6 -12, 10 -14, 13 -3 C 10 6, -3 5, 0 0 Z" fill="#285233" />
    </g>
  </svg>;
};

/** Embossed wax seal: the Coil spiral pressed in, or a lock while sealed. */
export const TomeWaxSeal: React.FC<{ locked?: boolean; className?: string }> = ({ locked, className = '' }) => {
  const gradient = `tome-wax-${React.useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return <svg className={`tome-seal ${className}`} viewBox="0 0 48 48" aria-hidden="true" data-locked={locked || undefined}>
    <defs>
      <radialGradient id={gradient} cx="0.35" cy="0.3" r="0.75">
        <stop offset="0" stopColor="#a82824" /><stop offset="0.7" stopColor="#6e1614" /><stop offset="1" stopColor="#4a0c0a" />
      </radialGradient>
    </defs>
    <path d="M24 2 C30 3 33 1 37 5 C41 8 45 10 45 16 C47 21 46 26 45 30 C44 36 41 40 36 43 C31 46 27 46 22 46 C16 45 11 43 7 38 C3 34 2 29 2 24 C2 18 4 13 8 9 C12 4 18 2 24 2 Z"
      fill={`url(#${gradient})`} stroke="#c93b36" strokeWidth="1" />
    <circle cx="24" cy="24" r="14" fill="none" stroke="#3a0907" strokeWidth="1.2" opacity="0.55" />
    {locked
      ? <path d="M18 22 v-3 a6 6 0 0 1 12 0 v3 M16.5 22 h15 v10 h-15 z" fill="none" stroke="#f7d6a1" strokeWidth="2" strokeLinejoin="round" />
      : <path d="M24 24 m0 0 a2 2 0 1 1 2 2 a4 4 0 1 1 -4 -4 a6 6 0 1 1 6 6 a8 8 0 1 1 -8 -8"
        fill="none" stroke="#f7d6a1" strokeWidth="1.8" strokeLinecap="round" />}
  </svg>;
};

/** Gilt star pin (plaques). */
export const TomeStar: React.FC = () => <svg className="tome-star" viewBox="0 0 16 16" aria-hidden="true">
  <path d="M8 1 L9.8 6 L15 6.2 L10.9 9.4 L12.4 14.6 L8 11.6 L3.6 14.6 L5.1 9.4 L1 6.2 L6.2 6 Z" fill="#f0d18a" stroke="#6b4a12" strokeWidth="0.8" />
</svg>;

/** A small leaf sprig: chapter heads and eyebrows. */
export const TomeSprig: React.FC = () => <svg className="tome-sprig" viewBox="0 0 16 16" aria-hidden="true">
  <path d="M2 14 C5 10 8 8 14 2" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" />
  <path d="M7 9 C5 5 8 3 11 4 C11 7 9 9 7 9 Z" fill="currentColor" opacity="0.85" />
  <path d="M4.5 11.5 C2 10 2 7.5 3.5 6.5 C5 8 5.5 10 4.5 11.5 Z" fill="currentColor" opacity="0.6" />
</svg>;

/** A hairline rule with a gilt lozenge at its centre, under a title block. */
export const TomeFlourish: React.FC<{ className?: string }> = ({ className = '' }) => <div className={`tome-flourish ${className}`} aria-hidden="true">
  <span className="tome-flourish-line" />
  <svg viewBox="0 0 36 14">
    <path d="M2 7 C8 2 12 2 14 7 C12 12 8 12 2 7 Z" fill="none" stroke="currentColor" strokeWidth="0.9" opacity="0.7" />
    <path d="M34 7 C28 2 24 2 22 7 C24 12 28 12 34 7 Z" fill="none" stroke="currentColor" strokeWidth="0.9" opacity="0.7" />
    <path d="M18 2 L22 7 L18 12 L14 7 Z" fill="#d8aa4a" stroke="#7a5410" strokeWidth="0.8" />
  </svg>
  <span className="tome-flourish-line" />
</div>;

/** The close mark, drawn rather than typed so it centres at every font size. */
export const TomeCloseMark: React.FC = () => <svg className="tome-close-mark" viewBox="0 0 16 16" aria-hidden="true">
  <path d="M4 4 L12 12 M12 4 L4 12" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
</svg>;
